import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCheckCircle, faCircle } from "@fortawesome/free-solid-svg-icons";
import { getTranslation } from "../../translations/getTranslations";
import { useNotification } from "../../notifications/Notifications";
import { useSelector, useDispatch } from "react-redux";
import { changePassword } from "../../services/userService";
import { logoutAndClearData } from "../../redux/slices/authSlice";

const ChangePasswordModal = ({ showModal, onClose }) => {
  const notify = useNotification();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationStatus, setValidationStatus] = useState({
    hasLower: false,
    hasUpper: false,
    hasNumeric: false,
    hasSpecialChar: false,
    isMinLength: false,
  });

  const language = useSelector(state => state.settings.language);
  const dispatch = useDispatch();

  const translation = key => getTranslation("changePasswordModal", key, language);

  useEffect(() => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
  }, []);

  const checkPasswordStrength = password => {
    setValidationStatus({
      hasLower: /[a-z]/.test(password),
      hasUpper: /[A-Z]/.test(password),
      hasNumeric: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      isMinLength: password.length >= 8,
    });
  };

  const handleNewPasswordChange = e => {
    const password = e.target.value;
    setNewPassword(password);
    checkPasswordStrength(password);
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      notify("error", translation("_newPasswordsDoNotMatch"));
      return;
    }
    if (oldPassword.trim() && newPassword.trim()) {
      try {
        const response = await changePassword(oldPassword, newPassword, confirmPassword);
        const successMessage = response.trim().replace(/^"+|"+$/g, "");
        notify("success", translation(successMessage));

        dispatch(logoutAndClearData());
      } catch (error) {
        const errorMessage = error.message.trim().replace(/^"+|"+$/g, "");
        notify("error", translation(errorMessage));
      }
    } else {
      notify("warn", translation("_fillFieldsAlert"));
    }
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-gray-800 bg-opacity-50">
      <div
        className={`relative w-[500px] rounded bg-backgroundPrimary p-6 text-center text-lg text-textPrimary shadow-lg`}
      >
        <button
          className={`absolute right-1 top-0 p-2 text-xl text-buttonCancel hover:text-buttonCancelHover`}
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2 className="mb-6 text-2xl font-semibold">{translation("_changePasswordModalTitle")}</h2>
        <div className="flex flex-col items-center justify-center">
          <input
            type="password"
            className={`w-[80%] rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
            value={oldPassword}
            onChange={e => setOldPassword(e.target.value)}
            placeholder={translation("_oldPassword")}
          />
          <input
            type="password"
            className={`mt-2 w-[80%] rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
            value={newPassword}
            onChange={handleNewPasswordChange}
            placeholder={translation("_newPassword")}
          />
          <input
            type="password"
            className={`mt-2 w-[80%] rounded border border-loginBorder bg-inputBackground p-2 text-textPrimary outline-none hover:bg-inputHover focus:outline-none`}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder={translation("_confirmNewPassword")}
          />
        </div>
        {/* Real-time validation messages */}
        <div className="mt-4 flex justify-center">
          <ul className="w-[90%] space-y-1 text-left text-xs">
            <li className={`flex items-center ${validationStatus.hasLower ? "text-green-900" : "text-textPrimary"}`}>
              {translation("_passwordValidationDesc")}
            </li>
            <li className={`flex items-center ${validationStatus.hasLower ? "text-green-900" : "text-textPrimary"}`}>
              <FontAwesomeIcon icon={validationStatus.hasLower ? faCheckCircle : faCircle} className="mr-2" />
              {translation("_passwordValidationLowLetter")}
            </li>
            <li className={`flex items-center ${validationStatus.hasUpper ? "text-green-900" : "text-textPrimary"}`}>
              <FontAwesomeIcon icon={validationStatus.hasUpper ? faCheckCircle : faCircle} className="mr-2" />
              {translation("_passwordValidationUpperLetter")}
            </li>
            <li className={`flex items-center ${validationStatus.hasNumeric ? "text-green-900" : "text-textPrimary"}`}>
              <FontAwesomeIcon icon={validationStatus.hasNumeric ? faCheckCircle : faCircle} className="mr-2" />
              {translation("_passwordValidationNumeric")}
            </li>
            <li
              className={`flex items-center ${validationStatus.hasSpecialChar ? "text-green-900" : "text-textPrimary"}`}
            >
              <FontAwesomeIcon icon={validationStatus.hasSpecialChar ? faCheckCircle : faCircle} className="mr-2" />
              {translation("_passwordValidationSpecialChar")}
            </li>
            <li className={`flex items-center ${validationStatus.isMinLength ? "text-green-900" : "text-textPrimary"}`}>
              <FontAwesomeIcon icon={validationStatus.isMinLength ? faCheckCircle : faCircle} className="mr-2" />
              {translation("_passwordValidationLength")}
            </li>
          </ul>
        </div>

        <p className="my-2 h-5 text-sm text-red-500"></p>
        <div>
          <button
            className={`mr-4 h-[40px] w-[130px] rounded bg-buttonSubmit p-1 text-buttonText hover:bg-buttonSubmitHover`}
            onClick={handlePasswordChange}
          >
            {translation("_save")}
          </button>
          <button
            className={`h-[40px] w-[130px] rounded bg-buttonCancel p-1 text-buttonText hover:bg-buttonCancelHover`}
            onClick={onClose}
          >
            {translation("_cancel")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
